import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import OrbitMap from "@/components/OrbitMap";
import { Card, Empty, Seg, sgn, NA, C } from "../ca/analysis/AnalysisUi";
import { eur0 } from "../ca/forecast/forecastUtils";
import { n0, n1, p1, s1, s2e, ZONE } from "./produitsFmt";
import GeoTree from "./GeoTree";

const MODES = [["ventes", "Ventes"], ["marge", "Marge"], ["mix", "Mix"]];
const SUB = { ventes: [["ca", "CA HT"], ["qte", "Quantités"]], marge: [["marge", "Marge €"], ["taux_marge", "Taux de marge"]], mix: [["mix", "Mix local"], ["var_ref", "vs référence"]] };
export const ZC = { over_strong: "#047857", over: C.green, neutral: C.amber, under: "#fb7185", under_strong: C.rose };

const avgRate = (stores) => {
  const ca = stores.reduce((a, s) => a + (s.marge === null ? 0 : s.ca), 0);
  const m = stores.reduce((a, s) => a + (s.marge ?? 0), 0);
  return ca ? (m / ca) * 100 : null;
};

const toBody = (period, filters, compare, mode, sel, measure) => ({
  period,
  filters: Object.fromEntries(Object.entries(filters).filter(([, v]) => v).map(([k, v]) => [k === "magasin" ? "corner_key" : k, v])),
  compare,
  product: mode === "mix" ? sel || null : null,
  measure,
});

export default function MapTab({ data, filters, compare, period, hideCosts, goTab }) {
  const [mode, setMode] = useState("ventes");
  const [measure, setMeasure] = useState("ca");
  const [sel, setSel] = useState("");
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(false);
  const [focus, setFocus] = useState(null);
  const bodyKey = JSON.stringify([period, filters, compare, mode, sel, measure]);
  const body = useMemo(() => toBody(period, filters, compare, mode, sel, measure), [bodyKey]); // eslint-disable-line

  useEffect(() => {
    if (mode === "mix" && !sel) { setD(null); return; }
    setLoading(true);
    api.post("/produits-analytics/map", body).then((r) => setD(r.data)).catch((e) => toast.error(e?.response?.data?.detail || "Erreur carte")).finally(() => setLoading(false));
  }, [body]); // eslint-disable-line

  const changeMode = (m) => { setMode(m); setMeasure(SUB[m][0][0]); };
  const stores = d?.stores || [];
  const sizeKey = mode === "mix" ? "ca" : measure;
  const mx = Math.max(...stores.map((s) => Math.abs(s[sizeKey] ?? 0)), 1);
  const avg = avgRate(stores);
  const color = (s) => {
    if (mode === "mix") return ZC[s.zone] || C.slate;
    if (mode === "marge" && measure === "taux_marge") return s.taux_marge === null || avg === null ? C.slate : s.taux_marge >= avg ? C.green : C.rose;
    return C.blue;
  };
  const points = stores.filter((s) => s.lat !== null && s.lat !== undefined).map((s) => ({
    id: s.key, lat: s.lat, lng: s.lng, color: color(s),
    radius: 5 + Math.sqrt(Math.max(0, s[sizeKey] ?? 0) / mx) * 16,
    popup: <Pop s={s} mode={mode} hideCosts={hideCosts} />,
  }));
  const title = mode === "mix" ? `Répartition de « ${d?.selection?.label} » — mix local vs référence (${p1(d?.selection?.mix_ref)})` : mode === "marge" ? "Marge théorique par magasin" : "Ventes par magasin";
  const subTitle = mode === "mix" ? "Couleur = zone de variation (seuils Administration › Analyse Produits) ; taille ∝ √CA du produit." : `Taille ∝ √${SUB[mode].find((x) => x[0] === measure)[1]}.`;

  return (
    <div className="space-y-4" data-testid="pa-map">
      <div className="bg-white border border-slate-200 rounded-2xl p-3 shadow-sm flex flex-wrap items-center gap-3" data-testid="pa-map-controls">
        <span className="text-[11px] font-semibold text-slate-500">Mode</span>
        <Seg value={mode} onChange={changeMode} opts={MODES.map(([k, l]) => [k, l, k === "marge" && hideCosts])} testid="pa-map-mode" />
        <span className="text-[11px] font-semibold text-slate-500">Indicateur</span>
        <Seg value={measure} onChange={setMeasure} opts={SUB[mode]} testid="pa-map-measure" />
        {mode === "mix" && (
          <select value={sel} onChange={(e) => setSel(e.target.value)} className="text-[12px] border border-slate-200 rounded-lg px-2 py-1.5 min-w-[240px]" data-testid="pa-map-select">
            <option value="">Choisir un produit ou une catégorie…</option>
            <optgroup label="Catégories">{data.categories.map((c) => <option key={c.key} value={`cat:${c.key}`}>{c.label}</option>)}</optgroup>
            <optgroup label="Produits">{data.products.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}</optgroup>
          </select>
        )}
        {loading && <span className="text-[11px] text-slate-400">Calcul…</span>}
        {d && <span className="ml-auto text-[11px] text-slate-500">{d.period.label} · {stores.length} magasin(s){d.no_gps.length ? ` · ${d.no_gps.length} sans coordonnées` : ""}</span>}
      </div>
      {mode === "mix" && !sel && <Empty text="Sélectionnez un produit ou une catégorie pour lire sa sur / sous-représentation par magasin." />}
      {d && (
        <>
          <div className="grid grid-cols-1 xl:grid-cols-12 gap-4">
            <Card className="xl:col-span-8" n="1" title={title} sub={subTitle} testid="pa-map-card">
              <OrbitMap height={420} testid="pa-map-orbit" points={points} onPointClick={(p) => setFocus(p.id)} />
              <Legend mode={mode} measure={measure} avg={avg} />
            </Card>
            <Reading d={d} mode={mode} setFocus={setFocus} goTab={goTab} />
          </div>
          <GeoTree d={d} measure={measure} mix={mode === "mix"} hideCosts={hideCosts} focus={focus} />
        </>
      )}
    </div>
  );
}

function Legend({ mode, measure, avg }) {
  if (mode === "mix") return <div className="flex flex-wrap gap-3 text-[10px] text-slate-600">{Object.entries(ZONE).map(([k, [l]]) => <span key={k} className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full" style={{ background: ZC[k] }} />{l}</span>)}</div>;
  if (mode === "marge" && measure === "taux_marge") return <div className="flex flex-wrap gap-3 text-[10px] text-slate-600"><span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-emerald-500" />≥ taux moyen réseau ({p1(avg)})</span><span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-rose-500" />&lt; taux moyen</span></div>;
  return <div className="text-[10px] text-slate-500">Cliquez sur un point pour le retrouver dans le tableau.</div>;
}

function Reading({ d, mode, setFocus, goTab }) {
  return (
    <Card className="xl:col-span-4" title={<span className="flex items-center gap-2"><span className="w-6 h-6 rounded-lg bg-violet-600 text-white grid place-items-center text-[10px]">★</span>Lecture de la carte</span>} testid="pa-map-reading">
      {d.reading.length === 0 && <Empty text="Pas assez de données pour une lecture." />}
      {d.reading.map((r, i) => (
        <button key={i} onClick={() => setFocus(r.keys[0] || null)} className="w-full text-left flex items-start gap-2 rounded-xl border border-slate-100 p-2.5 hover:bg-slate-50" data-testid={`pa-map-reading-${i}`}>
          <span className="w-5 h-5 rounded-full bg-blue-600 text-white grid place-items-center text-[10px] font-bold shrink-0">{i + 1}</span>
          <div><div className="text-[12px] font-semibold text-slate-800 leading-snug">{r.text}</div>{r.sub && <div className="text-[10px] text-slate-500 mt-0.5">{r.sub}</div>}</div>
        </button>
      ))}
      <div className="text-[9px] text-slate-400">Phrases générées par le moteur Orbit à partir des chiffres ci-dessous (aucune IA).</div>
      {mode === "mix" && <button onClick={() => goTab("reco")} className="text-[11px] font-semibold text-blue-700 hover:underline" data-testid="pa-map-reco-cta">Voir les recommandations →</button>}
    </Card>
  );
}

function Pop({ s, mode, hideCosts }) {
  return (
    <div className="text-[11px] min-w-[180px]">
      <b>{s.label}</b>
      <div className="text-slate-500">{s.ville || ""} · {s.secteur || ""} · {s.dr || ""}</div>
      <div>CA {eur0(s.ca)} · {n0(s.qte)} u · {n1(s.velocite)} u/j</div>
      {!hideCosts && <div>Marge {s.marge === null ? NA : eur0(s.marge)} · taux {p1(s.taux_marge)}</div>}
      {mode === "mix" && (
        <div>
          Mix {p1(s.mix)} vs réf. {p1(s.mix_ref)} → <b className={sgn(s.var_ref)}>{s1(s.var_ref)}</b>
          {s.zone && <span className={`ml-1 px-1 rounded text-[9px] ${ZONE[s.zone][1]}`}>{ZONE[s.zone][0]}</span>}
          {s.potentiel !== null && <div>Potentiel à mix comparable : <b className={sgn(s.potentiel)}>{s2e(s.potentiel)}</b></div>}
        </div>
      )}
      <div>Évol. CA {s1(s.evol_ca)}</div>
    </div>
  );
}
